<script lang="ts">
import { onMount } from 'svelte'

import Field from '@/components/form/Field.svelte'
import { createForm, FormControlType, INPUT_MAXLENGTH } from '@/modules/form'
import { getFormControlValidators, getFormControlValues } from '@/modules/form/utils'
import {
	createTicketFormControls,
	loadTicketFormFromStorage,
	removeTicketFormFromStorage,
	saveTicketFormFieldToStorage,
	submitTicketForm,
	type TicketForm,
	TicketFormOptions,
	type TicketFormValues,
} from '@/modules/ticket'
import { t } from '@/modules/translations'
import { visitor } from '@/modules/visitor'
import { printError } from '@/utils/console'

import AuthFormPrivacyNotice from '../authForm/AuthFormPrivacyNotice.svelte'

type Props = {
	id: string
	isSubmitting?: boolean
}

let { id, isSubmitting = $bindable(false) }: Props = $props()

const controls = createTicketFormControls($visitor)

const {
	form,
	errors,
	isSubmitting: isFormSubmitting,
	setValues,
	handleChange,
	handleSubmit,
} = createForm({
	initialValues: getFormControlValues<TicketFormValues, typeof controls>(controls),
	validators: getFormControlValidators(controls),
	onChange: (field: TicketForm, value: string) => saveTicketFormFieldToStorage(field, value),
	onSubmit: async (values) => {
		try {
			await submitTicketForm(values)
			removeTicketFormFromStorage()
		} catch (e) {
			printError(String(e))
		}
	},
})

onMount(() => {
	const storedTicketFormValues = loadTicketFormFromStorage()
	if (storedTicketFormValues) setValues(storedTicketFormValues, { shouldValidate: true })
})

$effect(() => {
	isSubmitting = $isFormSubmitting
})
</script>

<form {id} onsubmit={handleSubmit} class="mb-0">
	{#each controls as control, index (index)}
		{#if control.type === FormControlType.Text}
			<Field
				bind:value={$form[control.name]}
				name={control.name}
				type={control.subType}
				label={$t(control.label)}
				placeholder={control.placeholder ? $t(control.placeholder) : ''}
				maxLength={control.maxLength ?? INPUT_MAXLENGTH}
				errors={$errors}
				isTextarea={control.name === TicketFormOptions.Message}
				onInput={handleChange}
				onBlur={handleChange}
			/>
		{/if}
	{/each}
	<AuthFormPrivacyNotice label={$t('topBar.gdprTitle')} isControlHidden isTicketForm />
</form>
